/**
 * QMetrix dashboard-aligned tokens for the floating AI chat widget.
 * Matches card surfaces used across Jira, Standup, Release, and Tech Quality views.
 */

export const QUICK_PROMPTS_BY_PAGE = {
  qmetry360: [
    'How is release readiness trending?',
    'Summarize engineering score',
    'What are the top release risks?',
    'Show the latest dashboard highlights',
  ],
  engMetricsJira: [
    'How is cycle time trending?',
    'What is our current defect density?',
    'Summarize bug classification',
    'Show burndown and velocity trends',
  ],
  engMetricsGit: [
    'What is our PR approval rate?',
    'Show PRs merged without review',
    'Which pull requests are high risk?',
    'How is PR iteration time trending?',
  ],
  standup: [
    'What is the current Sprint Health?',
    'Show burndown trend',
    'What is story churn?',
    'Summarize Git engineering metrics',
  ],
  techQuality: [
    'What is our current bug rate?',
    'How is time to resolution trending?',
    'Summarize defect escape ratio',
    'What is our defect acceptance ratio?',
  ],
  release: [
    'Is this release ready?',
    'Summarize release risks',
  ],
  jira: [
    'Summarize open Jira issues',
    'What is blocked right now?',
  ],
  integration: [
    'Which integrations are connected?',
    'What still needs setup?',
    'Show connection health',
    'Which projects are linked?',
  ],
};

export const getQuickPrompts = (page) => QUICK_PROMPTS_BY_PAGE[page] || QUICK_PROMPTS_BY_PAGE.standup;

export const QUICK_PROMPTS = QUICK_PROMPTS_BY_PAGE.standup;

export const getChatSurfaceClasses = (theme) =>
  theme === 'dark'
    ? 'bg-[#182433] text-[#C8C8C8] border border-[#25384F] shadow-[0_1px_20px_0_rgba(0,0,0,0.35)]'
    : 'bg-[#FFFFFF] text-[#202020] border border-[#E5E5E5] shadow-[0_1px_20px_0_rgba(0,0,0,0.1)]';

export const getChatHeaderClasses = (theme) =>
  theme === 'dark'
    ? 'border-b border-[#25384F] bg-[#1B2D42]'
    : 'border-b border-[#E5E5E5] bg-[#F8FBFE]';

export const getChatFooterClasses = (theme) =>
  theme === 'dark' ? 'border-t border-[#25384F] bg-[#182433]' : 'border-t border-[#E5E5E5] bg-[#FFFFFF]';

export const getIconButtonClasses = (theme, { active = false } = {}) => {
  if (active) {
    return theme === 'dark'
      ? 'bg-[#066FD1] text-white hover:bg-[#2B8AE3]'
      : 'bg-[#066FD1] text-white hover:bg-[#054B8F]';
  }

  return theme === 'dark'
    ? 'text-[#A3B1C9] hover:bg-[#25384F] hover:text-white'
    : 'text-[#6C7A91] hover:bg-[#EFF8FE] hover:text-[#066FD1]';
};

export const getPrimaryButtonClasses = (theme, disabled = false) => {
  if (disabled) {
    return 'cursor-not-allowed bg-[#A3B1C9] text-white opacity-60';
  }

  return theme === 'dark'
    ? 'bg-[#066FD1] text-white hover:bg-[#2B8AE3]'
    : 'bg-[#066FD1] text-white hover:bg-[#054B8F]';
};

export const getInputShellClasses = (theme) =>
  theme === 'dark'
    ? 'border-[#25384F] bg-[#1B2D42] placeholder:text-[#8CA0B8] focus:border-[#48A7FF]'
    : 'border-[#D1E2F0] bg-white placeholder:text-[#6C7A91] focus:border-[#066FD1]';

export const getUserBubbleClasses = (theme) =>
  theme === 'dark' ? 'bg-[#066FD1] text-white' : 'bg-[#066FD1] text-white';

export const getAssistantBubbleClasses = (theme) =>
  theme === 'dark'
    ? 'bg-[#1B2D42] text-[#E8EEF5] border border-[#25384F]'
    : 'bg-[#F8FBFE] text-[#202020] border border-[#D1E2F0]';

export const getChipClasses = (theme) =>
  theme === 'dark'
    ? 'border border-[#25384F] bg-[#1B2D42] text-[#C8C8C8] hover:border-[#48A7FF] hover:text-white'
    : 'border border-[#D1E2F0] bg-white text-[#24527A] hover:border-[#066FD1] hover:text-[#066FD1]';

export const getContextBadgeClasses = (theme) =>
  theme === 'dark'
    ? 'bg-[#25384F]/80 text-[#C8C8C8]'
    : 'bg-[#EFF8FE] text-[#24527A]';

export const formatDataSourceLabel = (source) => {
  if (!source) {
    return '';
  }

  if (source === 'knowledge_base') {
    return 'Knowledge base';
  }

  if (source === 'ui_dashboard') {
    return 'Dashboard';
  }

  if (source.startsWith('api:')) {
    return source.replace('api:', '').replace(/_/g, ' ');
  }

  return source.replace(/_/g, ' ');
};
